import { useChatSessionStreamStateChangedSubscription } from "@qw/graphql";
import { type RefObject, useEffect, useRef, useState } from "react";
import { useEventCallback } from "usehooks-ts";

import { type AiSdkChatStatus, actionForStreamStateEvent } from "./action-for-stream-state-event";

interface UseStreamAttachParams {
  isReadOnly: boolean;
  sessionId: string;
  chatKey: string;
  status: AiSdkChatStatus;
  /** `useChat.resumeStream` — server-serialized per session, fast-fails 204 when nothing is attachable. */
  resumeStream: () => Promise<void>;
  /** `useChat.stop` — releases the SDK status from `submitted`/`streaming`. */
  originalStop: () => Promise<void>;
  /** Elicit-submit state (owned by the hub) that gates re-attach so it can't race the imperative resume. */
  isElicitationSubmitting: boolean;
  isElicitationSubmittingRef: RefObject<boolean>;
  /** False in compact playbook mode: keep server signals, but do not consume the live SSE body. */
  liveStreamEnabled: boolean;
}

interface UseStreamAttachResult {
  /** Re-attach the live SSE stream, self-gating (in-flight turn / elicit submit); safe to over-call. */
  ensureAttached: () => void;
  /** Synthetic stream-failure error layered on top of `useChat.error` (brief 004 PR2). */
  streamFailureError: Error | null;
}

/**
 * Owns the SSE stream-attach lifecycle for the Alby chat (extracted from `useMcpChat`, B-1458).
 *
 * Stream-attach strategy:
 *
 * (a) **Attach probe** — fire `resumeStream()` once per `chatKey` and whenever the live reader is
 *     re-enabled after compact mode. The server's `waitForState` fast-fails (204) when there's
 *     nothing to attach to. If the prior reader is still winding down, defer until its status clears.
 *
 * (b) **Server-push** — subscribe to the per-session `chatSessionStreamStateChanged` channel and
 *     `resumeStream()` on a STREAMING transition for this chat. Covers trigger-dispatched turns,
 *     post-mount elicitation-completion resumes, and multi-turn auto-attach. `actionForStreamStateEvent`
 *     gates by session match + STREAMING kind + formatVersion + AI SDK status + elicit-submitting.
 *
 * (c) **WebSocket reconnect / tab-return** — the shared `useConversationFreshness` controller (in the
 *     provider) calls `ensureAttached` on those edges. If the gates trip (status still `submitted`/
 *     `streaming`, or an elicit submit is in flight), `needsProbeRef` stays set and the status-edge
 *     effect drains it once the gates open — so a reconnect that raced a stale-but-active status can't
 *     leave the chat detached.
 *
 * Also owns `streamFailureError` — the synthetic failure surfaced from server-published FAILED events
 * (brief 004 PR2) — and its reset on the "failed turn is no longer active" edges (chatKey change /
 * status → submitted|streaming).
 */
export const useStreamAttach = ({
  isReadOnly,
  sessionId,
  chatKey,
  status,
  resumeStream,
  originalStop,
  isElicitationSubmitting,
  isElicitationSubmittingRef,
  liveStreamEnabled,
}: UseStreamAttachParams): UseStreamAttachResult => {
  // Compact mode can turn the live reader off and back on without changing `chatKey`. Treat each
  // enabled edge like a mount probe, and defer it when the previous reader is still winding down.
  const needsProbeRef = useRef(false);
  const lastAttachChatKeyRef = useRef<string | null>(null);

  useEffect(() => {
    if (!liveStreamEnabled) {
      lastAttachChatKeyRef.current = null;
      needsProbeRef.current = false;
      return;
    }
    if (isReadOnly || sessionId === "" || lastAttachChatKeyRef.current === chatKey) {
      return;
    }
    lastAttachChatKeyRef.current = chatKey;
    if (status === "submitted" || status === "streaming" || isElicitationSubmittingRef.current) {
      needsProbeRef.current = true;
      return;
    }
    needsProbeRef.current = false;
    void resumeStream().catch(() => undefined);
  }, [chatKey, isReadOnly, liveStreamEnabled, resumeStream, sessionId, status, isElicitationSubmittingRef]);

  useEffect(() => {
    if (!liveStreamEnabled && (status === "submitted" || status === "streaming")) {
      void originalStop();
    }
  }, [liveStreamEnabled, originalStop, status]);

  // Synthetic failure layered on top of `useChat.error` — populated from server-published FAILED
  // stream-state events (brief 004 PR2), covering pre-admit failures where no SSE stream connected.
  const [streamFailureError, setStreamFailureError] = useState<Error | null>(null);

  useChatSessionStreamStateChangedSubscription({
    variables: { chatSessionId: sessionId },
    skip: isReadOnly || sessionId === "",
    onData: ({ data }) => {
      const event = data.data?.chatSessionStreamStateChanged;
      const action = actionForStreamStateEvent({
        eventChatSessionId: event?.chatSessionId,
        eventKind: event?.kind,
        eventFormatVersion: event?.formatVersion,
        sessionId,
        hookStatus: status,
        // Ref read (not state). The ref is flipped synchronously inside `submitElicitationResponse`
        // before any await, so this read sees the latest value even when the subscription fires
        // before React has committed the `setIsElicitationSubmitting(true)` update.
        isElicitationSubmitting: isElicitationSubmittingRef.current,
      });
      if (action === "resume" && liveStreamEnabled) {
        void resumeStream();
      } else if (action === "stop_and_record" || action === "record_only") {
        // Surface the failure message so the chat shell's `ErrorChatSessionMessage` renders a
        // user-visible disclosure. Without this, pre-admit failures produce a silent input unlock.
        // Fires regardless of AI SDK status: the critical record-only windows are the elicit-submit
        // gap (hook stays at `ready` while the resumed GET hasn't yielded its first chunk) and the
        // SSE-error-then-FAILED race (status already `error` with AI SDK's generic message).
        setStreamFailureError(new Error(event?.error ?? "The stream failed."));
        if (action === "stop_and_record") {
          // Release `useChat.status` from `submitted`/`streaming` so "Alby is responding..."
          // disappears and the input unlocks. The AI SDK leaves any partial assistant message intact.
          void originalStop();
        }
      }
    },
  });

  // Tracks "a re-attach probe is pending but gated" — `ensureAttached` sets it when it can't attach
  // yet (in-flight turn / elicit submit), and the status-edge consumer below drains it once the
  // gates open.
  // Re-attach the live SSE stream, self-gating: suppress during an in-flight turn
  // (`submitted`/`streaming`) or an elicit submit, deferring via `needsProbeRef` so the status-edge
  // consumer below replays the probe once the gates open. Called by the shared
  // `useConversationFreshness` controller on the tab-return and WS-reconnect edges. Because this is a
  // `useEventCallback` it reads the latest committed `status` directly (no ref-mirror needed);
  // `isElicitationSubmittingRef` stays a ref because it flips synchronously mid-submit, before any
  // render. `resumeStream` is server-serialized per session and fast-fails 204, so a redundant call
  // is cheap.
  const ensureAttached = useEventCallback(() => {
    if (!liveStreamEnabled || isReadOnly || sessionId === "") {
      return;
    }
    if (status === "submitted" || status === "streaming") {
      needsProbeRef.current = true;
      return;
    }
    if (isElicitationSubmittingRef.current) {
      needsProbeRef.current = true;
      return;
    }
    needsProbeRef.current = false;
    void resumeStream();
  });

  // Status-edge consumer for a probe deferred by `ensureAttached`. If a tab-return / WS-reconnect
  // arrived while `status` was `submitted`/`streaming` or an elicit submit was mid-flight,
  // `needsProbeRef` stayed true; drain it (via `ensureAttached`, which re-checks the gates) the next
  // time `status`/`isElicitationSubmitting` transitions. Without it the probe would sit pending until
  // the next WS bounce and the chat would stay detached after status falls back to `ready`/`error`.
  useEffect(() => {
    if (needsProbeRef.current) {
      ensureAttached();
    }
  }, [status, isElicitationSubmitting, liveStreamEnabled, ensureAttached]);

  // Scope `streamFailureError` to the failed turn by clearing on the two edges that mark "the failed
  // turn is no longer the active one": (1) `chatKey` change (session switch / content-signature
  // reinit) and (2) AI SDK status transition into `submitted`/`streaming` (covers every new-turn
  // entry point — user, server-driven, and the mount probe). Tracked via the React-canonical
  // compare-previous-render pattern (runs synchronously in the render that observed the change).
  const [prevChatKey, setPrevChatKey] = useState(chatKey);
  const [prevStatus, setPrevStatus] = useState<AiSdkChatStatus>(status);
  if (chatKey !== prevChatKey) {
    setPrevChatKey(chatKey);
    setStreamFailureError(null);
  }
  if (status !== prevStatus) {
    setPrevStatus(status);
    if (status === "submitted" || status === "streaming") {
      setStreamFailureError(null);
    }
  }

  return { ensureAttached, streamFailureError };
};
